import { createSlice } from '@reduxjs/toolkit';
import { default as cloneDeep } from 'lodash/cloneDeep';
import { ICartData, IUndoCartItem } from '../../interfaces';
import { CartSteps } from '../../enums/cart-steps';
import { Step2Tabs } from '../../enums/step-2-tabs';
import { getCountriesAndStates } from '../../../../../domain/addresses/addresses-provider';
import { ICountry } from '../../../../../domain/addresses/interfaces';
import { PaymentTabsEnum } from '../../enums/payment-tabs-enum';
import { CreditCardTabTabsEnum } from '../../enums/credit-card-tab';
import { formatCartData, getInitialCartData } from '../data-provider';
import { IFormControl } from '../../../../../ui/forms/interfaces';
import { createControl } from '../../../../../domain/validation/validation';
import store from '../store';
import { getInitialSelectedPaymentTab, getProductsCount } from '../../domain/shopping-cart-provider';
import { UPDATE_MINI_CART_COUNT_EVENT } from '../../../../site-header/top-minicart/ui/main';

interface IState{

    // data from HTML
    cartData: ICartData,
    countries: ICountry[],

    // selected tabs / steps
    cartStep: CartSteps,
    step2Tab: Step2Tabs,
    selectedPaymentTab: PaymentTabsEnum,
    selectedCreditCardTab: CreditCardTabTabsEnum,

    // undo cart items
    removedItems: IUndoCartItem[],

    // in-app purchase
    isInAppPurchase: boolean,
    showNoSessionPanel: boolean,

    // counters
    localTime: number,

    // refer friend
    // https://wavesaudio.atlassian.net/browse/DSWEB-3176
    // https://dde169.axshare.com/#g=1&p=refer_a_friend_-_flows
    isReferCreditChecked: boolean,
    isReferRecurringChecked: boolean,
    referCreditControl: IFormControl,
    showCreditNavError: boolean,
}

const cartData = getInitialCartData();

// https://wavesaudio.atlassian.net/browse/DSWEB-2248
if(cartData.errorCoupons){
    cartData.errorCoupons = [];
}

/**
 * initial state
 */
const initialState: IState = {

    // data from HTML
    cartData,
    countries: getCountriesAndStates(),

    // selected tabs / steps
    cartStep: (cartData.skipStep1 && cartData.isAuthenticated) ?  CartSteps.Third : CartSteps.First, //CartSteps.First,
    step2Tab: Step2Tabs.Login,
    selectedPaymentTab: getInitialSelectedPaymentTab(cartData), // PaymentTabsEnum.CreditCard
    selectedCreditCardTab: CreditCardTabTabsEnum.SavedCreditCards, // CreditCardTabTabsEnum.SavedCreditCards

    // undo cart items - https://wavesaudio.atlassian.net/browse/DSWEB-1017
    removedItems: [],

    // in-app purchase
    isInAppPurchase: window.location.pathname.startsWith('/inapp/cart'), // !!cartData.inAppPurchaseResult,
    showNoSessionPanel: false,

    // counters
    localTime: (new Date()).getTime(),

    // refer friend
    // https://wavesaudio.atlassian.net/browse/DSWEB-3176
    // https://dde169.axshare.com/#g=1&p=refer_a_friend_-_flows
    isReferCreditChecked: false,
    isReferRecurringChecked: false,
    showCreditNavError: false,
    referCreditControl: createControl('referCreditControl', cartData.priceData.referCredit.toString(), {
        // required: true,
        number: true,
        decimalPlaces: 2,
        minValue: 0,
        maxValue: () => {
            return Number(store?.getState()?.root?.cartData?.priceData?.referCredit) || 0;
        },
        maxValueMessage: 'The maximum value cannot exceed $$1',
        minValueMessage: 'The minimum value cannot be less than $$1',
    }),
};

const rootSlice = createSlice({
    name: 'root',
    initialState,
    reducers: {

        main(state, action) {
            return {
                ...state,
                ...action.payload,
            }
        },

        setCartData(state, action) {

            const updatedCartData = formatCartData(cloneDeep(action.payload));

            if(getProductsCount(state.cartData.cartItems) !== getProductsCount(updatedCartData.cartItems)) {

                // update top min-cart products count -----------------
                document.dispatchEvent(new CustomEvent(UPDATE_MINI_CART_COUNT_EVENT));
            }

            return {
                ...state,
                cartData: updatedCartData,
                selectedPaymentTab: getInitialSelectedPaymentTab(updatedCartData),
            }
        },

        setControl(state, action) {
            return {
                ...state,
                [action.payload.controlName]: cloneDeep(action.payload.control),
            }
        },
    }
});

export default rootSlice;